import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App'
import './index.css'
import { adminConsole } from './lib/utils/adminConsole'
import { performanceMonitor } from './lib/performance/performanceMonitor'
import RecaptchaService from './lib/security/recaptchaService'
import { getRecaptchaConfig } from './config/appConfig'

// Initialize console restrictions early in the application lifecycle
// This ensures console access is controlled from the very start
// The adminConsole system will automatically restrict console.log/warn/info for non-admins
adminConsole.init();

// Initialize reCAPTCHA service dynamically (not in HTML)
// This prevents exposing the site key in the HTML source
try {
  const recaptchaConfig = getRecaptchaConfig();
  if (recaptchaConfig.siteKey) {
    RecaptchaService.initialize(recaptchaConfig);
    console.log('[Security] reCAPTCHA service initialized');
  } else {
    console.warn('[Security] reCAPTCHA site key not configured');
  }
} catch (error) {
  console.error('[Security] Failed to initialize reCAPTCHA:', error);
}

// Initialize performance monitoring
if (import.meta.env.PROD) {
  // Log performance report after page load
  // These logs will only be visible to admins (controlled by adminConsole)
  window.addEventListener('load', () => {
    setTimeout(() => {
      try {
        console.log(performanceMonitor.generateReport());
      } catch (error) {
        console.warn('[Performance] Failed to generate report:', error);
      }
    }, 2000);
  });

  // Cleanup performance monitor on page hide (modern best practice)
  window.addEventListener('pagehide', () => {
    try {
      performanceMonitor.destroy();
    } catch (error) {
      console.warn('[Performance] Failed to cleanup monitor:', error);
    }
  });
}

// Service Worker - DISABLED for frequent updates
// Only clear service workers and caches once, not on every page load
if ('serviceWorker' in navigator) {
  // Check if we've already cleaned up (use sessionStorage to track per-session)
  const hasCleanedUp = sessionStorage.getItem('sw_cleanup_done');
  
  if (!hasCleanedUp) {
    window.addEventListener('load', async () => {
      try {
        // Unregister ALL service workers first
        const registrations = await navigator.serviceWorker.getRegistrations();
        if (registrations.length > 0) {
          console.log('[SW] Unregistering service workers...');
          for (const registration of registrations) {
            await registration.unregister();
          }
          console.log('[SW] Service workers unregistered');
        }

        // Clear all caches only if service workers were found
        if (registrations.length > 0) {
          console.log('[SW] Clearing caches...');
          const cacheNames = await caches.keys();
          await Promise.all(cacheNames.map(cacheName => caches.delete(cacheName)));
          console.log('[SW] Caches cleared');
        }
        
        // Mark cleanup as done for this session
        sessionStorage.setItem('sw_cleanup_done', 'true');
      } catch (error) {
        console.error('[SW] Cleanup failed:', error);
      }
    });
  }
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
)
